import React, { useEffect } from 'react';
import flatMap from 'lodash/flatMap';

type Ref =
  | React.MutableRefObject<HTMLElement | null | undefined>
  | React.RefObject<HTMLElement | null>;

// useOnOutsideClick aka useOnClickOutside
export function useOnOutsideClick(
  rootRef: Ref | (Ref | string)[],
  callback: (e: MouseEvent) => void,
  enabled = true,
  checkOutsideFn?: (target: HTMLElement, e: MouseEvent) => boolean
) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const checkOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      const roots = (
        Array.isArray(rootRef)
          ? flatMap(rootRef, ref => {
              if (typeof ref === 'string') {
                const elements = document.querySelectorAll(ref);
                return Array.from(elements);
              }
              return ref.current;
            })
          : [rootRef.current]
      ).filter(node => !!node) as HTMLElement[];

      if (roots.length === 0) {
        return;
      }

      if (
        // any of the roots contains our target
        roots.some(root => root.contains(target)) ||
        // or it has ignore-enforcing ancestors
        target.closest('.ignore-react-onclickoutside') ||
        // or the custom checking method says the element is inside
        (checkOutsideFn && !checkOutsideFn(target, e))
      ) {
        // this was an inside click, nothing to see here
      } else {
        callback(e);
      }
    };

    document.addEventListener('mousedown', checkOutsideClick);
    document.addEventListener('touchstart', checkOutsideClick);
    return () => {
      document.removeEventListener('mousedown', checkOutsideClick);
      document.removeEventListener('touchstart', checkOutsideClick);
    };
  }, [callback, rootRef, enabled, checkOutsideFn]);
}
